import {capitalize} from "@mui/material";
import {Field, useFormikContext} from "formik";
import {TextField} from "formik-mui";
import GenericSelect from "../data/GenericSelect";
import {useTranslation} from "react-i18next";
import {useStimmenHandling} from "../../hooks/stimmen/useStimmenHandling";
import {useSpeechModelHandling} from "../../hooks/speech_model/useSpeechModelHandling";

/**
 * Minimum shape that the Formik values object must satisfy
 * to use these shared inbound-configuration fields.
 */
export interface InboundConfigFormValues {
    sprache: string;
    stimmeId?: string;
    speechModel?: string;
    empathie: number;
}

interface Props {
    canEdit: boolean;
}

/**
 * Shared Formik-based fields for inbound call configuration.
 * Works inside any Formik form whose values extend InboundConfigFormValues.
 */
export default function FieldsInboundConfig({canEdit}: Props) {
    const {t: translation} = useTranslation("aufgaben");
    const {setFieldValue, values} = useFormikContext<InboundConfigFormValues & Record<string, unknown>>();
    const {stimmenOptions, selectedStimme} = useStimmenHandling(values);
    const {speechModelOptions, selectedSpeechModel} = useSpeechModelHandling(values.speechModel);

    return (
        <>
            <Field
                component={TextField}
                label={capitalize(translation("task-attributes-language"))}
                name="sprache"
                disabled={!canEdit}
            />
            <Field
                component={GenericSelect}
                name="stimmeId"
                label={capitalize(translation("task-attributes-voice-id"))}
                options={stimmenOptions}
                getOptionLabel={(option: {id: string; name: string}) => option.name}
                getSecondaryLabel={(option: {id: string; name: string}) => option.id}
                setSelected={(value: string) => setFieldValue("stimmeId", value)}
                initialSelected={selectedStimme}
                required={false}
                disabled={!canEdit}
            />
            <Field
                key={selectedSpeechModel?.id ?? "default"}
                component={GenericSelect}
                name="speechModel"
                label={capitalize(translation("task-attributes-speech-model"))}
                options={speechModelOptions}
                getOptionLabel={(option: {id: string; name: string}) => option.name}
                getSecondaryLabel={(option: {id: string; name: string}) => option.id}
                setSelected={(value: string) => setFieldValue("speechModel", value)}
                initialSelected={selectedSpeechModel}
                required={false}
                disabled={!canEdit}
            />
            <Field
                component={TextField}
                label={capitalize(translation("task-attributes-empathy"))}
                name="empathie"
                type="number"
                inputProps={{min: 0, max: 1, step: 0.1}}
                disabled={!canEdit}
            />
        </>
    );
}
